export interface DocumentTitleObserver {
  /** Reports the current title again, even when it did not change. */
  refresh(): void;
  /** Stops observing `<head>`. */
  disconnect(): void;
}

/**
 * Reports `document.title` now and again whenever it changes.
 *
 * Next.js commits `<title>` in a passive effect (Pages Router) or streams it
 * with the metadata (App Router), so it can land after the route change that
 * started a new view; observing `<head>` catches that late commit. Head
 * mutations that leave the title unchanged are ignored. Call `refresh()` when
 * a new view starts with the same title, since the observer will not fire.
 */
export function observeDocumentTitle(
  onTitle: (title: string) => void
): DocumentTitleObserver {
  let lastTitle = document.title;
  const refresh = () => {
    lastTitle = document.title;
    onTitle(lastTitle);
  };

  refresh();

  const observer = new MutationObserver(() => {
    if (document.title !== lastTitle) refresh();
  });
  observer.observe(document.head, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  return { refresh, disconnect: () => observer.disconnect() };
}
